import * as classNames from 'classnames'
import * as React from 'react'
import * as moment from 'moment'
import { Link, RouteComponentProps } from 'react-router-dom'
import { useTable, usePagination, useSortBy, Column } from 'react-table'
import { IRiderFilters, IRiderSearchObject, IMembershipRiderObject } from '../../types'
import Close from '../icons/vehicle/Close'
import Success from '../icons/vehicle/Success'
import ReactTablePagination from '../ui/ReactTablePagination'
import ContextMenu from '../ui/ContextMenu'

export interface ITableRidersJoinRequestProps {
  filters: IRiderFilters
  riders: IRiderSearchObject[]
  totalItems: number
  fetchRiders: (filters: IRiderFilters) => Promise<any>
  approve: (rider: IRiderSearchObject) => void
  notApprove: (rider: IRiderSearchObject) => void
}

export interface ITableRidersState {
  pages: number
}

interface IProps<T extends object> {
  columns: Array<Column<T>>
  data: T[]
  loading: boolean
  filters: IRiderFilters
  pageCount: number
  fetchData: (params: { filters: IRiderFilters; pageIndex: number; pageSize: number }) => void
}

const PAGE_SIZE = 50

function Table<T extends object>({
  columns,
  data,
  fetchData,
  loading,
  filters,
  pageCount: controlledPageCount
}: IProps<T>) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    state
  } = useTable<T>(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      manualSortBy: true,
      manualPagination: true,
      pageCount: controlledPageCount
    },
    useSortBy,
    usePagination
  )

  const { pageIndex, pageSize, sortBy } = state

  React.useEffect(() => {
    fetchData({ filters, pageIndex, pageSize })
  }, [filters, pageIndex, pageSize])

  React.useEffect(() => {
    const sortParams = sortBy.map(s => {
      return s.id + '-' + (s.desc ? 'desc' : 'asc')
    })
    fetchData({ filters: { ...filters, sortBy: sortParams }, pageIndex, pageSize })
  }, [sortBy])

  return (
    <React.Fragment>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  className={classNames('', {
                    sortable: column.canSort,
                    sortedAsc: column.isSortedDesc === false,
                    sortedDesc: column.isSortedDesc === true
                  })}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {loading ? (
            <tr>
              <td>Loading...</td>
            </tr>
          ) : (
            rows.map(
              (row, i) =>
                +prepareRow(row) || (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell: any, index) => {
                      return (
                        <td key={`riderJoinRequestColumn-${cell.column.index}`} {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </td>
                      )
                    })}
                  </tr>
                )
            )
          )}
        </tbody>
      </table>
      <ReactTablePagination
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        canNext={canNextPage}
        canPrevious={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        rowCount={rows.length}
      />
    </React.Fragment>
  )
}

const buildMembershipCard = (membership: IMembershipRiderObject) => {
  const serviceName = membership && membership.service && membership.service.name ? membership.service.name : ''
  const externalId = membership && membership.externalRiderId ? `, ${membership.externalRiderId}` : ''
  return (
    <div className='m1 rounded border-grey4 bg-white font-grey3'>
      <div className='h6 bold ml2 mr2 mt02 mb02'>
        {serviceName}
        {externalId}
      </div>
    </div>
  )
}

const renderEmail = (rider: IRiderSearchObject) => {
  if (!rider || !rider.email) {
    return ''
  }

  return rider.email
}

const renderUsername = (rider: IRiderSearchObject) => {
  if (!rider || !rider.username) {
    return ''
  }

  return rider.username
}

const renderCreatedDate = (rider: IRiderSearchObject) => {
  if (!rider || !rider.createdAt) {
    return ''
  }
  return moment(rider.createdAt).format('MMM DD YYYY')
}

export default function RiderJoinRequestTable(props: ITableRidersJoinRequestProps) {
  const [data, setData] = React.useState([])
  const [loading, setLoading] = React.useState(false)

  const columns = React.useMemo(
    () => [
      {
        id: 'name',
        Header: 'Name',
        width: 200,
        accessor: (rider: IRiderSearchObject) => {
          return <Link to={`/riders/${rider.id}/profile/info`}>{rider.name}</Link>
        }
      },
      {
        id: 'phone',
        Header: 'Phone Number',
        accessor: 'phone',
        disableSortBy: true,
        className: 'flex items-center'
      },
      {
        id: 'email',
        Header: 'Email',
        accessor: (rider: IRiderSearchObject) => renderEmail(rider),
        className: 'RidersListItemEmail flex items-center'
      },
      {
        id: 'username',
        Header: 'Username',
        accessor: (rider: IRiderSearchObject) => renderUsername(rider),
        className: 'RidersListItemEmail flex items-center'
      },
      {
        id: 'memberships',
        Header: 'Memberships',
        accessor: 'membership',
        className: 'flex items-center',
        disableSortBy: true,
        Cell: (data: any) => {
          //   const rider = data.row.original
          //   if (!rider.serviceMemberships || !rider.serviceMemberships.length) {
          //     return ''
          //   }
          //   return (
          //     <div className='flex'>
          //       {rider.serviceMemberships.map((mem: any, key: number) => (
          //         <div key={key}>{buildMembershipCard(mem)}</div>
          //       ))}
          //     </div>
          //   )
          // }
          const rider = data.row.original
          if (!rider.serviceMemberships || !rider.serviceMemberships.length) {
            return ''
          }
          let temp = ''

          rider.serviceMemberships.map((v: IMembershipRiderObject, key: number) => {
            temp = v.status === 'NEW' ? temp + v.service.name + ', ' : temp + ''
          })
          return temp.slice(0, -2)
        }
      },
      {
        id: 'createdDate',
        Header: 'Created Date',
        accessor: (rider: IRiderSearchObject) => renderCreatedDate(rider),
        disableSortBy: true,
        className: 'flex items-center'
      },
      {
        id: 'status',
        Header: '',
        accessor: 'status',
        className: 'overflow--visible',
        disableSortBy: true,
        Cell: (data: any) => {
          return <CellContextMenu rider={data.row.original} approve={approve} notApprove={notApprove} />
        }
      }
    ],
    []
  )

  const CellContextMenu = (props: {
    rider: IRiderSearchObject
    approve: (item: IRiderSearchObject) => () => void
    notApprove: (item: IRiderSearchObject) => () => void
  }) => {
    return (
      <div className='flex items-center'>
        <ContextMenu>
          <div className='MenuItem flex cursor--pointer'>
            <div className='onDeactivateOption' onClick={props.approve(props.rider)}>
              <Success className='icon--16x16 cursor--pointer' />
              <span className='font-dark-grey ml1'>Approve</span>
            </div>
          </div>
          <div className='MenuItem flex cursor--pointer'>
            <div className='onActivateOption' onClick={props.notApprove(props.rider)}>
              <Close className='icon--16x16 cursor--pointer' />
              <span className='font-dark-grey ml1'>Not Approve</span>
            </div>
          </div>
        </ContextMenu>
      </div>
    )
  }
  const approve = (item: IRiderSearchObject) => () => {
    return props.approve(item)
  }

  const notApprove = (item: IRiderSearchObject) => () => {
    return props.notApprove(item)
  }

  const fetchData = React.useCallback(async ({ filters, pageSize, pageIndex }) => {
    setLoading(true)
    await props.fetchRiders({ ...filters, skip: pageIndex * pageSize, take: pageSize })
    setLoading(false)
  }, [])

  const pageCount = Math.ceil(props.totalItems / PAGE_SIZE)
  return (
    <React.Fragment>
      <div className='react-table'>
        <Table
          columns={columns}
          data={props.riders}
          fetchData={fetchData}
          filters={props.filters}
          loading={loading}
          pageCount={pageCount}
        />
      </div>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/components/riders/RiderJoinRequestTable.tsx